import React, { useState } from 'react';

import { MoreOutlined } from '@ant-design/icons';
import { Flex } from '@revenuemarket/cds';
import { Colors, GrayColors, SemanticColors, typographyStyle } from '@revenuemarket/design';
import { formatMoney } from '@revenuemarket/utils';
import { Select, Tag, Typography } from 'antd';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import { Pannel } from './Pannel';
interface DummyTerm {
  tradingPeriod: number;
  discountRate: number;
  payout: number;
  repaymentSchedules: {
    repaymentAmount: number;
    principalAmount: number;
    discountAmount: number;
  }[];
}

const DUMMY_TERMS: DummyTerm[] = [
  {
    tradingPeriod: 1,
    discountRate: 0.0159,
    payout: 500000000,
    repaymentSchedules: [
      {
        repaymentAmount: 508078447,
        principalAmount: 500000000,
        discountAmount: 8078447,
      },
    ],
  },
  {
    tradingPeriod: 2,
    discountRate: 0.0237,
    payout: 500000000,
    repaymentSchedules: [
      {
        repaymentAmount: 256068831,
        principalAmount: 250000000,
        discountAmount: 6068831,
      },
      {
        repaymentAmount: 256068831,
        principalAmount: 250000000,
        discountAmount: 6068831,
      },
    ],
  },
  {
    tradingPeriod: 3,
    discountRate: 0.0315,
    payout: 500000000,
    repaymentSchedules: [
      {
        repaymentAmount: 172087421,
        principalAmount: 166666667,
        discountAmount: 5420754,
      },
      {
        repaymentAmount: 172087421,
        principalAmount: 166666667,
        discountAmount: 5420754,
      },
      {
        repaymentAmount: 172087421,
        principalAmount: 166666667,
        discountAmount: 5420754,
      },
    ],
  },
  {
    tradingPeriod: 4,
    discountRate: 0.0392,
    payout: 500000000,
    repaymentSchedules: [
      {
        repaymentAmount: 130099917,
        principalAmount: 125000000,
        discountAmount: 5099917,
      },
      {
        repaymentAmount: 130099917,
        principalAmount: 125000000,
        discountAmount: 5099917,
      },
      {
        repaymentAmount: 130099917,
        principalAmount: 125000000,
        discountAmount: 5099917,
      },
      {
        repaymentAmount: 130099917,
        principalAmount: 125000000,
        discountAmount: 5099917,
      },
    ],
  },
  {
    tradingPeriod: 5,
    discountRate: 0.0468,
    payout: 500000000,
    repaymentSchedules: [
      {
        repaymentAmount: 104909778,
        principalAmount: 100000000,
        discountAmount: 4909778,
      },
      {
        repaymentAmount: 104909778,
        principalAmount: 100000000,
        discountAmount: 4909778,
      },
      {
        repaymentAmount: 104909778,
        principalAmount: 100000000,
        discountAmount: 4909778,
      },
      {
        repaymentAmount: 104909778,
        principalAmount: 100000000,
        discountAmount: 4909778,
      },
      {
        repaymentAmount: 104909778,
        principalAmount: 100000000,
        discountAmount: 4909778,
      },
    ],
  },
  {
    tradingPeriod: 6,
    discountRate: 0.0543,
    payout: 500000000,
    repaymentSchedules: [
      {
        repaymentAmount: 88118148,
        principalAmount: 83333333,
        discountAmount: 4784815,
      },
      {
        repaymentAmount: 88118148,
        principalAmount: 83333333,
        discountAmount: 4784815,
      },
      {
        repaymentAmount: 88118148,
        principalAmount: 83333333,
        discountAmount: 4784815,
      },
      {
        repaymentAmount: 88118148,
        principalAmount: 83333333,
        discountAmount: 4784815,
      },
      {
        repaymentAmount: 88118148,
        principalAmount: 83333333,
        discountAmount: 4784815,
      },
      {
        repaymentAmount: 88118148,
        principalAmount: 83333335,
        discountAmount: 4784813,
      },
    ],
  },
];

const TermSimulation = React.memo(({ ellipsis }: { ellipsis?: boolean }) => {
  const [dummyTerm, setDummyTerm] = useState(DUMMY_TERMS[2]);

  const renderSchedule = (
    schedule: { repaymentAmount: number; principalAmount: number; discountAmount: number },
    i: number,
  ) => (
    <Flex direction="column" gap={2} key={i}>
      <Flex direction="row" justify="space-between" align="center">
        <Typography.Text style={{ ...typographyStyle['14_600'], color: Colors.gray800 }}>
          {`${formatMoney(schedule.repaymentAmount)}`}
        </Typography.Text>
        <Flex direction="row" gap={4}>
          <Typography.Text style={{ ...typographyStyle['12_400'], color: Colors.gray600 }}>
            {i === 0 ? `지급일+${i + 1}개월 차` : `${i + 1}개월 차`}
          </Typography.Text>
        </Flex>
      </Flex>
      <Flex direction="column" gap={2}>
        {i === 0 && (
          <>
            <Typography.Text
              style={{
                ...typographyStyle['12_400'],
                color: Colors.gray500,
              }}
            >
              {`${formatMoney(schedule.principalAmount)} (원금)`}
            </Typography.Text>
            <Typography.Text
              style={{
                ...typographyStyle['12_400'],
                color: Colors.gray500,
              }}
            >
              {`${formatMoney(schedule.discountAmount)} (할인금)`}
            </Typography.Text>
          </>
        )}
      </Flex>
    </Flex>
  );

  return (
    <Flex direction="column" gap={20}>
      <Pannel>
        <Typography.Text style={{ ...typographyStyle['13_400'], color: Colors.gray800 }}>
          예시 시나리오 지급 및 회수 일정
        </Typography.Text>
        <Flex direction="row" justify="space-between">
          <Flex direction="column" gap={4}>
            <Typography.Text style={{ ...typographyStyle['13_400'], color: Colors.gray800 }}>
              할인율
            </Typography.Text>
            <Typography.Text
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                flex: '1 0 0',
                alignSelf: 'stretch',
                ...typographyStyle['14_600'],
                color: Colors.gray800,
              }}
            >
              {`${(dummyTerm.discountRate * 100).toFixed(2)}%`}
            </Typography.Text>
          </Flex>
          <Flex direction="column" gap={4}>
            <Typography.Text style={{ ...typographyStyle['13_400'], color: Colors.gray800 }}>
              회수 기간
            </Typography.Text>
            <Select
              style={{
                width: 106,
              }}
              options={DUMMY_TERMS.map((term) => ({
                label: `${term.tradingPeriod}개월`,
                value: term.tradingPeriod,
              }))}
              value={dummyTerm.tradingPeriod}
              onChange={(value) => {
                setDummyTerm({
                  ...DUMMY_TERMS.find((term) => term.tradingPeriod === value)!,
                });
                mixpanel.track(MIXPANEL_EVENT.HOME.CHANGE_FINANCE_SIMULATION_SCENARIO, {
                  scenario: value,
                });
              }}
            />
          </Flex>
        </Flex>
        <Flex direction="column" gap={4}>
          <div style={{ display: 'inline-block' }}>
            <Tag color={SemanticColors.plus500} style={{ fontSize: 13 }}>
              지급받는 금액
            </Tag>
          </div>
          <Flex direction="row" justify="space-between" align="center">
            <Typography.Text
              style={{
                ...typographyStyle['14_600'],
                color: SemanticColors.plus500,
              }}
            >
              {`${formatMoney(dummyTerm.payout)}원`}
            </Typography.Text>
            <Typography.Text
              style={{
                ...typographyStyle['12_400'],
                color: GrayColors.gray500,
              }}
            >
              지급일
            </Typography.Text>
          </Flex>
        </Flex>
        <Flex direction="column" gap={8}>
          <div style={{ display: 'inline-block' }}>
            <Tag color={GrayColors.grayOpacity07} style={{ color: Colors.gray800, fontSize: 13 }}>
              회수되는 금액
            </Tag>
          </div>
          {ellipsis ? (
            <>
              {renderSchedule(dummyTerm.repaymentSchedules[0], 0)}
              {dummyTerm.repaymentSchedules.length > 1 &&
                renderSchedule(dummyTerm.repaymentSchedules[1], 1)}
              {dummyTerm.repaymentSchedules.length === 3 &&
                renderSchedule(dummyTerm.repaymentSchedules[2], 2)}
              {dummyTerm.repaymentSchedules.length > 3 && (
                <>
                  <Flex direction="row" justify="flex-end" align="center">
                    <MoreOutlined style={{ color: Colors.gray500 }} />
                  </Flex>
                  {renderSchedule(
                    dummyTerm.repaymentSchedules[dummyTerm.repaymentSchedules.length - 1],
                    dummyTerm.repaymentSchedules.length - 1,
                  )}
                </>
              )}
            </>
          ) : (
            dummyTerm.repaymentSchedules.map((schedule, i) => renderSchedule(schedule, i))
          )}
        </Flex>
      </Pannel>
    </Flex>
  );
});

export default TermSimulation;
